/**
 * AshramaVashitaInfoTable Component
 * Displays D40 Ashrama Vashita system for maternal lineage influence
 * 4 ASHRAMA (life stages) × 10 VASHITA (influences) = 40 divisions
 */

import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  HStack
} from '@chakra-ui/react';
import { JatakaResponse } from '../../../../../models/api';
import {
  calculateAshramaVashitas,
  AshramaVashitaData,
  AshramaVashitaAttributes,
  ASHRAMA_DESCRIPTIONS,
  VASHITA_DESCRIPTIONS
} from '../data/AshramaVashitaTableData';
import {
  getAshramaColor,
  getVashitaColor
} from '../../../../../config/ashramaVashitaColors';
import { BLACK_ONLY_ASHTAMSHA_COLORS, getAshtamshaPlanetColor } from '../../../../../config/ashtamshaColors';
import { createPlanetDisplay } from '../helpers/planetDisplayHelper';
import {
  TABLE_WRAPPER_INLINE_STYLES,
  TABLE_CONTAINER_STYLES,
  TABLE_BASE_STYLES,
  TABLE_HEADER_ALT_STYLES,
  TABLE_ROW_HOVER_STYLES,
  TABLE_TD_BASE_STYLES,
  TABLE_TD_SMALL_STYLES,
  TABLE_TD_CENTER_STYLES,
  PLANET_TEXT_STYLES,
  EMPTY_MESSAGE_BOX_STYLES,
  EMPTY_CELL_TEXT_STYLES,
  COLUMN_STYLES
} from './styles/tableStyles';

export interface AshramaVashitaInfoTableProps {
  apiResponse?: JatakaResponse | null;
}

/**
 * Group divisions by amsha number for display
 */
interface AmshaRow {
  amshaNumber: number;
  ashramaType: any;
  vashitaType: any;
  planets: AshramaVashitaAttributes[];
}

/**
 * Create all 40 amsha rows with planets grouped
 */
function groupByAmshaNumber(divisions: AshramaVashitaAttributes[]): AmshaRow[] {
  const rows: AmshaRow[] = [];
  const planetsByAmsha: Record<number, AshramaVashitaAttributes[]> = {};

  // Initialize all 40 amshas
  for (let i = 1; i <= 40; i++) {
    planetsByAmsha[i] = [];
  }

  // Group planets by amsha number
  divisions.forEach(div => {
    planetsByAmsha[div.amshaNumber].push(div);
  });

  // Create rows with all info
  for (let i = 1; i <= 40; i++) {
    const planetsInAmsha = planetsByAmsha[i];
    const ashramaNumber = Math.floor((i - 1) / 10) + 1;
    const vashitaNumber = ((i - 1) % 10) + 1;

    const ashramaTypes = ['BRAHMACHARYA', 'GRIHASTHA', 'VANAPRASTHA', 'SANYASA'];
    const vashitaTypes = ['JANA', 'JANMA', 'AYUS', 'CHITTA', 'PARISHKURA', 'DHARMA', 'RIDDHI', 'PRANIDHANA', 'KARMA', 'ADHI_MUKTI'];

    rows.push({
      amshaNumber: i,
      ashramaType: ashramaTypes[ashramaNumber - 1],
      vashitaType: vashitaTypes[vashitaNumber - 1],
      planets: planetsInAmsha
    });
  }

  return rows;
}

export const AshramaVashitaInfoTable: React.FC<AshramaVashitaInfoTableProps> = ({ apiResponse }) => {
  if (!apiResponse) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>No chart data available</Text>
      </Box>
    );
  }

  // Get current hora planet for highlighting
  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;

  // Calculate Ashrama Vashita divisions
  const ashramaVashitaData: AshramaVashitaData = calculateAshramaVashitas(apiResponse);

  if (!ashramaVashitaData.divisions.length) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>Unable to calculate Ashrama Vashita divisions</Text>
      </Box>
    );
  }

  // Group all divisions by amsha number (1-40)
  const amshaRows = groupByAmshaNumber(ashramaVashitaData.divisions);

  return (
    <Box {...TABLE_WRAPPER_INLINE_STYLES}>
      {/* Ashrama Vashita Table */}
      <TableContainer {...TABLE_CONTAINER_STYLES}>
        <Table {...TABLE_BASE_STYLES}>
          <Thead {...TABLE_HEADER_ALT_STYLES}>
            <Tr>
              <Th {...COLUMN_STYLES.large}>Planets</Th>
              <Th {...COLUMN_STYLES.xsmall}>Part</Th>
              <Th {...COLUMN_STYLES.medium}>ASHRAMA</Th>
              <Th {...COLUMN_STYLES.medium}>VASHITA</Th>
              <Th {...COLUMN_STYLES.xxlarge}>Maternal Influence</Th>
            </Tr>
          </Thead>
          <Tbody>
            {amshaRows.map((row) => {
              const ashramaInfo = ASHRAMA_DESCRIPTIONS[row.ashramaType as keyof typeof ASHRAMA_DESCRIPTIONS];
              const vashitaInfo = VASHITA_DESCRIPTIONS[row.vashitaType as keyof typeof VASHITA_DESCRIPTIONS];
              const ashramaColor = getAshramaColor(row.ashramaType);
              const vashitaColor = getVashitaColor(row.vashitaType);

              return (
                <Tr key={row.amshaNumber} _hover={TABLE_ROW_HOVER_STYLES}>
                  {/* Planets column */}
                  <Td {...TABLE_TD_BASE_STYLES} minW="80px">
                    {row.planets.length > 0 ? (
                      <HStack spacing={1} flexWrap="wrap">
                        {row.planets.map((division, index) => {
                          // Use centralized planet display helper (DRY)
                          const planetDisplay = createPlanetDisplay(division.planet, currentHoraPlanet);
                          const colors = getAshtamshaPlanetColor(planetDisplay.planetType, BLACK_ONLY_ASHTAMSHA_COLORS);

                          return (
                            <Text
                              key={`${division.planet.short_name}-${index}`}
                              {...PLANET_TEXT_STYLES}
                              color={colors.color}
                              bg={colors.backgroundColor}
                              border={colors.borderColor ? `1px solid` : 'none'}
                              borderColor={colors.borderColor}
                            >
                              {planetDisplay.displayText}
                            </Text>
                          );
                        })}
                      </HStack>
                    ) : (
                      <Text {...EMPTY_CELL_TEXT_STYLES}></Text>
                    )}
                  </Td>

                  {/* Part Number */}
                  <Td {...TABLE_TD_CENTER_STYLES} fontWeight="normal">
                    <Text fontSize="xs">
                      {row.amshaNumber}
                    </Text>
                  </Td>

                  {/* ASHRAMA with color */}
                  <Td {...TABLE_TD_SMALL_STYLES}>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color={ashramaColor}
                    >
                      {ashramaInfo.sanskrit}
                    </Text>
                  </Td>

                  {/* VASHITA with color */}
                  <Td {...TABLE_TD_SMALL_STYLES}>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color={vashitaColor}
                    >
                      {vashitaInfo.sanskrit}
                    </Text>
                  </Td>

                  {/* Maternal Influence */}
                  <Td {...TABLE_TD_SMALL_STYLES} fontWeight="normal">
                    <Text fontSize="xs" color="gray.700">
                      {vashitaInfo.sphere} in {ashramaInfo.sanskrit.toLowerCase()} stage
                    </Text>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
